import { Button, Editable, EDIT_ACTIONS, H5, H6, Icon, Menu, MenuItem, Spinner } from "@alternative/components";
import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import InfoEditor from "./InfoEditor";
import { apiKeyState, designListState, toastState } from "../../recoil/filemanager/atoms";
import {
  convertFeetInchToNumber,
  convertNumberToFeetInch,
  filterFileArrFromExt,
  getNameFromPath,
  renameVariation,
  reorder,
  updateFileProps,
  updateNodeProps,
} from "../../utils";
import { AppProviderAPI, FileManagerAPI } from "../../api";
import { acceptedExtensions, baseUrl } from "../../constants";
import convert from "convert-units";
import { getDesignThumb } from "../../api/appProvider";
import FileNode, { DraggableFileNode } from "./FileNode";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import classNames from "classnames";
import { ContextMenu } from "@blueprintjs/core";
import { IconNames } from "@alternative/icons";
import InputButton from "./InputButton";
import { fetchApiKey, fetchInterfaceElements } from "../../recoil/filemanager/selectors";
import { v4 } from "uuid";
import pako from "pako";

const INFO_AREA = "fm-info-area";
const DES_PROPS = "des-props";
const DES_VARS = "des-vars-area";
const DES_STORY = "des-story";
const EditableInput = (props) => {
  const { defaultValue, onSubmit, headingTitle = true, ...otherprops } = props;
  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  const inpRef = useRef();
  const headingClassname = `${INFO_AREA}__${DES_PROPS}__name`;
  const title = headingTitle ? (
    <H5 className={headingClassname}>{value}</H5>
  ) : (
    <span className={headingClassname}>{value}</span>
  );
  return (
    <Editable editAction={EDIT_ACTIONS.SINGLE_CLICK} childRef={inpRef} {...otherprops} text={title} type="input">
      <input
        ref={inpRef}
        type="text"
        value={value}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (!onSubmit) return;
          if (e.key === "Enter") {
            onSubmit(value);
          }
        }}
        onBlur={() => {
          if (!onSubmit) return;
          onSubmit(value);
        }}
      />
    </Editable>
  );
};
const Input = (props) => {
  const { defaultValue, onSubmit, onChange } = props;
  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  return (
    <input
      type="text"
      value={value}
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        setValue(e.target.value);
        if (onChange) onChange(e.target.value);
      }}
      onKeyDown={(e) => {
        if (!onSubmit) return;
        if (e.key === "Enter") {
          onSubmit(value);
        }
      }}
      onBlur={() => {
        if (!onSubmit) return;
        onSubmit(value);
      }}
    />
  );
};
export const DisplayUnit = (props) => {
  const {
    physicalWidth,
    physicalHeight,
    unit,
    handleChangeWidth,
    handleChangeHeight,
    handleChangeUnit,
    handleConfirmChange,
  } = props;
  const displayWidth = convertNumberToFeetInch(physicalWidth, unit);
  const displayHeight = convertNumberToFeetInch(physicalHeight, unit);
  return (
    <div className={`${INFO_AREA}__${DES_PROPS}__dims`}>
      <div>
        Width
        <Input defaultValue={displayWidth} onChange={handleChangeWidth} />
      </div>
      <div>
        Height
        <Input defaultValue={displayHeight} onChange={handleChangeHeight} />
      </div>
      <div>
        Unit
        <select value={unit} onChange={(e) => handleChangeUnit && handleChangeUnit(e.target.value)}>
          <option value="ft">ft</option>
          <option value="cm">cm</option>
        </select>
      </div>
      <Button intent="primary" tertiary text="Apply" onClick={handleConfirmChange} />
    </div>
  );
};
const EmptyState = () => {
  return (
    <div className={INFO_AREA}>
      <H5>Select a design to view/edit its properties</H5>
    </div>
  );
};

const DesignVariationList = () => {
  const [designlist, setDesignList] = useRecoilState(designListState);
  const apiKey = useRecoilValue(apiKeyState);
  const [isDragging, setIsDragging] = useState(false);
  const { designTree, selectedDesigns, selectedVariations } = designlist;
  const currentDesign = selectedDesigns[0];
  const { variations } = currentDesign;
  const onDragEnd = async (result) => {
    setIsDragging(false);
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const vars = reorder(variations.vars, result.source.index, result.destination.index);
    const newVariations = { ...variations, vars };
    const { updatedTree, updatedNode } = updateFileProps(designTree, selectedDesigns[0], "variations", newVariations);
    setDesignList({
      ...designlist,
      designTree: updatedTree,
      selectedDesigns: [{ ...selectedDesigns[0], variations: newVariations }],
      selectedFolders: [updatedNode],
    });
    await FileManagerAPI.setFileOrder({
      key: apiKey,
      path: vars[0].location,
      fileOrder: vars.map((item) => item.fullPath),
    });
  };
  const handleChangeName = async (varNode, name) => {
    const ren = await FileManagerAPI.renameFile({ key: apiKey, oldfile: varNode.fullPath, newfilename: name });
    if (!ren) return;
    const { updatedTree, updatedNode, updatedDesign } = renameVariation({
      designTree,
      design: selectedDesigns[0],
      varNode,
      name,
    });
    setDesignList({
      ...designlist,
      designTree: updatedTree,
      selectedFolders: [updatedNode],
      selectedDesigns: [updatedDesign],
    });
  };
  const handleDelSelVariation = async () => {
    if (!selectedVariations.length) return;
    let deletedFiles = [];
    for (let variation of selectedVariations) {
      const file = variation.fullPath;
      console.info("delete file ", file);

      const del = await FileManagerAPI.deleteFile({ key: apiKey, file });
      // const del = true;
      if (del) deletedFiles.push(variation);
    }
    const newVars = variations.vars.filter((item) => selectedVariations.find((selVar) => selVar.id !== item.id));
    const newDesignNode = { ...selectedDesigns[0], variations: { ...variations, vars: newVars } };
    const { updatedTree, updatedNode } = updateFileProps(designTree, selectedDesigns[0], "variations", newVars);
    setDesignList({
      ...designlist,
      designTree: updatedTree,
      selectedFolders: [updatedNode],
      selectedDesigns: [newDesignNode],
    });
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    ContextMenu.show(
      <Menu>
        <MenuItem
          text="delete"
          disabled={!selectedVariations.length}
          onClick={(e) => {
            ContextMenu.hide();
            handleDelSelVariation();
          }}
        />
      </Menu>,
      { left: e.clientX, top: e.clientY },
      () => {}
    );
  };
  return (
    <div className={`${INFO_AREA}__${DES_VARS}__variations`}>
      <FileNode
        isEditable={false}
        file={{ ...currentDesign, name: `${currentDesign.name} (Original Design)` }}
        isSelected={!selectedVariations.length}
        onClick={() => {
          setDesignList({ ...designlist, selectedVariations: [] });
        }}
      />

      <DragDropContext onDragEnd={onDragEnd} onBeforeDragStart={() => setIsDragging(true)}>
        <Droppable droppableId="variations">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <div
                onContextMenu={handleContextMenu}
                className={classNames("file-list-container", {
                  "file-list-container--dragging": isDragging,
                })}
              >
                {variations.vars.map((variation, i) => (
                  <DraggableFileNode
                    key={variation.id}
                    file={variation}
                    index={i}
                    isSelected={selectedVariations.find((item) => item.id === variation.id)}
                    handleChangeName={(name) => handleChangeName(variation, name)}
                    onClick={async (e) => {
                      //TODO: handle async action and update entire tree after getting thumb
                      setDesignList({ ...designlist, selectedVariations: [variation] });
                      if (!variation.thumbUrl || variation.props) {
                        const varia = await getDesignThumb(apiKey, [variation]);
                        setDesignList({ ...designlist, selectedVariations: varia });
                      }
                    }}
                  />
                ))}
              </div>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const DesignPropsSection = () => {
  const [designlist, setDesignList] = useRecoilState(designListState);
  const apiKey = useRecoilValue(apiKeyState);
  const { designTree, selectedDesigns, selectedVariations } = designlist;
  const currentDesign = selectedVariations.length ? selectedVariations[0] : selectedDesigns[0];
  const { name, thumbUrl, props } = currentDesign;
  const [designDims, setDesignDims] = useState({
    physicalWidth: props.PhysicalWidth,
    physicalHeight: props.PhysicalHeight,
    unit: props.Unit,
  });
  useEffect(() => {
    let physicalWidth = props.PhysicalWidth;
    let physicalHeight = props.PhysicalHeight;
    let unit = props.Unit;
    if (props.Unit === "in") {
      physicalWidth = convert(physicalWidth)
        .from(unit)
        .to("ft");
      physicalHeight = convert(physicalHeight)
        .from(unit)
        .to("ft");
      unit = "ft";
    }
    setDesignDims({ physicalWidth, physicalHeight, unit });
  }, [props]);
  const handleChangeName = async (val) => {
    const file = currentDesign;
    const ren = await FileManagerAPI.renameFile({ key: apiKey, oldfile: file.fullPath, newfilename: val });
    if (!ren) return;
    if (!selectedVariations.length) {
      const { updatedTree, updatedNode } = updateFileProps(designTree, file, "name", val);
      const updatedDesign = updatedNode.files.find((item) => item.id === file.id);
      setDesignList({
        ...designlist,
        designTree: updatedTree,
        selectedFolders: [updatedNode],
        selectedDesigns: [{ ...updatedDesign, thumbUrl: selectedDesigns[0].thumbUrl, props: selectedDesigns[0].props }],
      });
    } else {
      const { updatedTree, updatedNode, updatedDesign } = renameVariation({
        designTree,
        design: selectedDesigns[0],
        varNode: file,
        name: val,
      });
      setDesignList({
        ...designlist,
        designTree: updatedTree,
        selectedFolders: [updatedNode],
        selectedDesigns: [updatedDesign],
      });
    }
  };
  const handleChangeUnit = (unit) => {
    const width = convert(designDims.physicalWidth)
      .from(designDims.unit)
      .to(unit);
    const height = convert(designDims.physicalHeight)
      .from(designDims.unit)
      .to(unit);
    setDesignDims({ physicalWidth: width, physicalHeight: height, unit: unit });
  };
  const handleChangeWidth = async (width) => {
    let physicalWidth = convertFeetInchToNumber(width, designDims.unit);
    let physicalHeight = (physicalWidth * designDims.physicalHeight) / designDims.physicalWidth;
    let unit = designDims.unit;
    setDesignDims({ ...designDims, physicalWidth, physicalHeight, unit });
  };
  const handleChangeHeight = async (height) => {
    let physicalHeight = convertFeetInchToNumber(height, designDims.unit);
    let physicalWidth = (physicalHeight * designDims.physicalWidth) / designDims.physicalHeight;

    let unit = designDims.unit;
    setDesignDims({ ...designDims, physicalHeight, physicalWidth, unit });
  };
  const handleConfirmChange = async () => {
    const { physicalWidth, physicalHeight, unit } = designDims;
    await FileManagerAPI.changeDesignDims({
      key: apiKey,
      file: currentDesign.fullPath,
      physicalWidth,
      physicalHeight,
      unit,
    });
    await AppProviderAPI.clearCache({ apiKey, files: [currentDesign.fullPath], mode: "designs" });
  };
  return (
    <>
      <div className={`${INFO_AREA}__des-thumb`}>
        <img className={`${INFO_AREA}__des-thumb-image`} src={`${baseUrl}/${thumbUrl}`} alt="thumb" />
      </div>
      <div className={`${INFO_AREA}__${DES_PROPS}`}>
        <EditableInput defaultValue={name} onSubmit={(val) => handleChangeName(val)} />
        <DisplayUnit
          physicalHeight={designDims.physicalHeight}
          physicalWidth={designDims.physicalWidth}
          unit={designDims.unit}
          handleChangeHeight={handleChangeHeight}
          handleChangeWidth={handleChangeWidth}
          handleChangeUnit={handleChangeUnit}
          handleConfirmChange={handleConfirmChange}
        />
        {props.DesignColors && (
          <div className={`${INFO_AREA}__${DES_PROPS}__colors`}>{props.DesignColors.length} Colors</div>
        )}
      </div>
    </>
  );
};
const DesignStorySection = () => {
  const [designList, setDesignList] = useRecoilState(designListState);
  const key = useRecoilValue(apiKeyState);
  const { selectedDesigns, designTree } = designList;
  const currentDesign = selectedDesigns[0];
  const handleSaveStory = async (story) => {
    if (!selectedDesigns.length) return;
    const prevStory = selectedDesigns[0].story;
    if (prevStory === story) return;
    const { updatedNode, updatedTree } = updateFileProps(designTree, selectedDesigns[0], "story", story);

    setDesignList({
      ...designList,
      designTree: updatedTree,
      selectedFolders: [updatedNode],
      selectedDesigns: [{ ...currentDesign, story }],
    });

    const storypath = `${currentDesign.location}/.${currentDesign.name}`;
    await FileManagerAPI.addFolder({ key, path: storypath });
    const info_ = {
      story: story,
      tags: null,
      customizable: true,
    };

    await FileManagerAPI.addFolderInfo({ key, path: storypath, info: info_ });
  };
  return (
    <div className={`${INFO_AREA}__${DES_STORY}`}>
      <H6>Design Story</H6>
      <div className={`${INFO_AREA}__${DES_STORY}__editor-area`}>
        <InfoEditor rawState={currentDesign.story} handleSaveStory={handleSaveStory} />
      </div>
    </div>
  );
};
const PreviewDesign = () => {
  const [designList, setDesignList] = useRecoilState(designListState);
  const interfaceElements = useRecoilValue(fetchInterfaceElements);
  const forceUnit =
    interfaceElements.flags.fileManager && interfaceElements.flags.fileManager.forceUnit
      ? interfaceElements.flags.fileManager.forceUnit
      : "";
  const key = useRecoilValue(apiKeyState);
  const [toastListState, setToastLisState] = useRecoilState(toastState);
  const { uploadStatusToast } = toastListState;
  const { selectedDesigns, selectedVariations, designTree } = designList;
  const currentDesign = selectedVariations.length ? selectedVariations[0] : selectedDesigns[0];
  const { variations } = selectedDesigns[0];
  const { name, filename, thumbUrl, props } = currentDesign;
  const hasVariations = variations.vars && variations.vars.length;
  const handleUploadVariation = (e) => {
    const handleUpload = async () => {
      const acceptedExt = !interfaceElements.isJpeg ? acceptedExtensions.CTF : acceptedExtensions.JPEG;
      const fileArray = filterFileArrFromExt(Array.from(e.target.files), acceptedExt.arr);
      if (!fileArray.length) return;
      const design = selectedDesigns[0];

      const similarDesignsPath = `${design.location}/.${design.name}`;
      await FileManagerAPI.addFolder({ key, path: similarDesignsPath });

      const filesToUpload = fileArray.map((item) => item.name);
      let newUploadStatusToast = { ...uploadStatusToast, files: filesToUpload.length, progress: 0, canDismiss: false };
      setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });

      let filesUploaded = [];
      for (let index = 0; index < fileArray.length; index++) {
        const file = fileArray[index];
        const uploaded = await FileManagerAPI.uploadFile({ key, file: file, path: similarDesignsPath, forceUnit });
        if (uploaded) {
          filesUploaded.push(file.name);

          newUploadStatusToast = {
            ...newUploadStatusToast,
            progress: ((filesUploaded.length / filesToUpload.length) * 100).toFixed(1),
          };

          setToastLisState({ ...toastListState, uploadStatusToast: newUploadStatusToast });
        }
      }
      const newVars = fileArray.map((item) => {
        const name = getNameFromPath(item.name);
        const fullPath = `${similarDesignsPath}/${item.name}`;
        const location = similarDesignsPath;
        const type = "file";
        const filename = item.name;
        return { name, fullPath, location, type, filename, id: v4() };
      });
      const oldVars = variations.vars || [];
      const newVariations = { ...variations, vars: [...oldVars, ...newVars] };
      const newDesignNode = { ...design, variations: newVariations };
      const { updatedTree, updatedNode } = updateFileProps(designTree, design, "variations", newVariations);
      setToastLisState({
        ...toastListState,
        uploadStatusToast: { ...newUploadStatusToast, canDismiss: true, progress: 100 },
      });
      setDesignList({
        ...designList,
        designTree: updatedTree,
        selectedFolders: [updatedNode],
        selectedDesigns: [newDesignNode],
      });
    };
    handleUpload();
  };
  return (
    <div className={`${INFO_AREA}__des-info`}>
      <div className={`${INFO_AREA}__des-name`}>{filename}</div>
      {thumbUrl && props ? (
        <DesignPropsSection />
      ) : (
        <div className={`${INFO_AREA}__loading`}>
          <Spinner size="lg" />
        </div>
      )}
      <div className={`${INFO_AREA}__${DES_VARS}`}>
        <div className={`${INFO_AREA}__${DES_VARS}__heading`}>
          <H6>Design Variations</H6>
          <InputButton
            buttonProps={{
              minimal: true,
            }}
            inputProps={{
              onInput: handleUploadVariation,
              accept: !interfaceElements.isJpeg ? acceptedExtensions.CTF.ext : acceptedExtensions.JPEG.ext,
              multiple: true,
            }}
          >
            <Icon icon={IconNames.PLUS_CIRCLE} />
          </InputButton>
        </div>
        {hasVariations ? (
          <DesignVariationList />
        ) : (
          <span>No Design Variation found. Click on the plus icon above to upload</span>
        )}
      </div>
      <DesignStorySection />
    </div>
  );
};

const PreviewFolder = () => {
  const [designlist, setDesignlist] = useRecoilState(designListState);
  const { designTree, selectedFolders } = designlist;
  const key = useRecoilValue(apiKeyState);
  if (!selectedFolders.length) return <EmptyState />;
  const currentFolder = selectedFolders[0];
  const { name } = currentFolder;
  const handleSaveStory = (story) => {
    if (!selectedFolders.length) return;
    const prevStory = selectedFolders[0].story;
    if (prevStory === story) return;
    const { updatedNode, updatedTree } = updateNodeProps(designTree, selectedFolders[0], "story", story);
    let selFolders = [...selectedFolders];
    selFolders[0] = updatedNode;
    setDesignlist({ ...designlist, designTree: updatedTree, selectedFolders: selFolders });
    console.log(story);

    const info_ = {
      story: story,
      tags: null,
      customizable: true,
    };
    FileManagerAPI.addFolderInfo({ key, path: updatedNode.fullPath, info: info_ });
  };
  return (
    <div>
      <div className={`${INFO_AREA}__des-name`}>{name}</div>
      <div className={`${INFO_AREA}__${DES_STORY}`}>
        <H6>Folder Story</H6>
        <div className={`${INFO_AREA}__${DES_STORY}__editor-area`}>
          <InfoEditor rawState={selectedFolders[0].story} handleSaveStory={handleSaveStory} />
        </div>
      </div>
    </div>
  );
};
const PreviewSection = (props) => {
  const { onBackToExplorug, className = "" } = props;
  const designlist = useRecoilValue(designListState);
  const { selectedDesigns } = designlist;
  return (
    <div className={classNames(INFO_AREA, className)}>
      {selectedDesigns.length ? <PreviewDesign /> : <PreviewFolder />}
      <div>
        <Button intent="primary" text="Back to exploRUG" onClick={onBackToExplorug} />
      </div>
    </div>
  );
};

export default PreviewSection;
